import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag, Progress, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined, DollarOutlined, EyeOutlined, LineChartOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '../utils/axios';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface Marketing {
  id: string;
  name: string;
  type: 'EMAIL_CAMPAIGN' | 'SOCIAL_MEDIA' | 'ADVERTISING' | 'REFERRAL' | 'CONTENT_MARKETING';
  description?: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
  budget?: number;
  spentAmount?: number;
  startDate?: string;
  endDate?: string;
  targetAudience?: string;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  leadsGenerated?: number;
  content?: string;
  imageUrl?: string;
  landingPageUrl?: string;
  lastAnalyzedAt?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Metrics {
  totalCampaigns: number;
  activeCampaigns: number;
  totalBudget: number;
  totalSpent: number;
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  totalLeads: number;
  avgClickThroughRate: number;
  avgConversionRate: number;
}

const MarketingManagement: React.FC = (): JSX.Element => {
  const [campaigns, setCampaigns] = useState<Marketing[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAnalyticsModalVisible, setIsAnalyticsModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Marketing | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Marketing | null>(null);
  const [form] = Form.useForm();
  const [analyticsForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalBudget: 0,
    totalSpent: 0,
    totalImpressions: 0,
    totalClicks: 0,
    totalConversions: 0,
    totalLeads: 0,
    avgClickThroughRate: 0,
    avgConversionRate: 0
  });

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: Marketing[] }>('/marketing');
      if (response.data.success) {
        let filteredCampaigns = response.data.data;
        
        // Apply filters
        if (searchText) {
          filteredCampaigns = filteredCampaigns.filter(campaign => 
            campaign.name.toLowerCase().includes(searchText.toLowerCase()) ||
            campaign.description?.toLowerCase().includes(searchText.toLowerCase()) ||
            campaign.targetAudience?.toLowerCase().includes(searchText.toLowerCase())
          );
        }
        
        if (statusFilter) {
          filteredCampaigns = filteredCampaigns.filter(campaign => campaign.status === statusFilter);
        }
        
        if (typeFilter) {
          filteredCampaigns = filteredCampaigns.filter(campaign => campaign.type === typeFilter);
        }
        
        setCampaigns(filteredCampaigns);
        calculateMetrics(filteredCampaigns);
      } else {
        message.error('Failed to fetch marketing campaigns');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      message.error('Failed to fetch marketing campaigns');
    } finally {
      setLoading(false);
    }
  }, [searchText, statusFilter, typeFilter]);

  const calculateMetrics = (data: Marketing[]) => {
    const totalCampaigns = data.length;
    const activeCampaigns = data.filter(campaign => campaign.status === 'ACTIVE').length;
    const totalBudget = data.reduce((sum, campaign) => sum + (campaign.budget || 0), 0);
    const totalSpent = data.reduce((sum, campaign) => sum + (campaign.spentAmount || 0), 0);
    const totalImpressions = data.reduce((sum, campaign) => sum + (campaign.impressions || 0), 0);
    const totalClicks = data.reduce((sum, campaign) => sum + (campaign.clicks || 0), 0);
    const totalConversions = data.reduce((sum, campaign) => sum + (campaign.conversions || 0), 0);
    const totalLeads = data.reduce((sum, campaign) => sum + (campaign.leadsGenerated || 0), 0);
    
    const avgClickThroughRate = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgConversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    
    setMetrics({
      totalCampaigns,
      activeCampaigns,
      totalBudget,
      totalSpent,
      totalImpressions,
      totalClicks,
      totalConversions,
      totalLeads,
      avgClickThroughRate,
      avgConversionRate
    });
  };

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleEdit = (campaign: Marketing) => {
    setEditingCampaign(campaign);
    setIsModalVisible(true);
    form.setFieldsValue({
      ...campaign,
      dateRange: campaign.startDate && campaign.endDate 
        ? [dayjs(campaign.startDate), dayjs(campaign.endDate)]
        : null,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/marketing/${id}`);
      message.success('Campaign deleted successfully');
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      message.error('Failed to delete campaign');
    }
  };

  const handleCreate = () => {
    setEditingCampaign(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      delete formattedValues.dateRange;

      if (editingCampaign) {
        await api.put(`/marketing/${editingCampaign.id}`, formattedValues);
        message.success('Campaign updated successfully');
      } else {
        await api.post('/marketing', formattedValues);
        message.success('Campaign created successfully');
      }
      setIsModalVisible(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      message.error('Failed to save campaign');
    }
  };

  const handleAnalytics = (campaign: Marketing) => {
    setSelectedCampaign(campaign);
    setIsAnalyticsModalVisible(true);
    analyticsForm.setFieldsValue({
      impressions: campaign.impressions || 0,
      clicks: campaign.clicks || 0,
      conversions: campaign.conversions || 0,
      leadsGenerated: campaign.leadsGenerated || 0,
    });
  };

  const handleAnalyticsUpdate = async () => {
    try {
      const values = await analyticsForm.validateFields();
      await api.patch(`/marketing/${selectedCampaign?.id}/analytics`, values);
      message.success('Analytics updated successfully');
      setIsAnalyticsModalVisible(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error updating analytics:', error);
      message.error('Failed to update analytics');
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      DRAFT: 'default',
      ACTIVE: 'green',
      PAUSED: 'orange',
      COMPLETED: 'blue',
      CANCELLED: 'red',
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getTypeColor = (type: string) => {
    const colors = {
      EMAIL_CAMPAIGN: 'blue',
      SOCIAL_MEDIA: 'purple',
      ADVERTISING: 'orange',
      REFERRAL: 'green',
      CONTENT_MARKETING: 'cyan',
    };
    return colors[type as keyof typeof colors] || 'default';
  };

  const calculateCTR = (clicks: number, impressions: number) => {
    return impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : '0.00';
  };

  const calculateConversionRate = (conversions: number, clicks: number) => {
    return clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : '0.00';
  };

  const calculateBudgetUtilization = (spent: number, budget: number) => {
    return budget > 0 ? ((spent / budget) * 100).toFixed(1) : '0.0';
  };

  const columns: ColumnsType<Marketing> = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          <small style={{ color: '#888' }}>{record.description}</small>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>
          {type.replace('_', ' ')}
        </Tag>
      ),
      filters: [
        { text: 'Email Campaign', value: 'EMAIL_CAMPAIGN' },
        { text: 'Social Media', value: 'SOCIAL_MEDIA' },
        { text: 'Advertising', value: 'ADVERTISING' },
        { text: 'Referral', value: 'REFERRAL' },
        { text: 'Content Marketing', value: 'CONTENT_MARKETING' },
      ],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
      filters: [
        { text: 'Draft', value: 'DRAFT' },
        { text: 'Active', value: 'ACTIVE' },
        { text: 'Paused', value: 'PAUSED' },
        { text: 'Completed', value: 'COMPLETED' },
        { text: 'Cancelled', value: 'CANCELLED' },
      ],
    },
    {
      title: 'Budget',
      key: 'budget',
      render: (_, record) => (
        <div>
          <div>${(record.budget || 0).toLocaleString()}</div>
          <Progress 
            percent={parseFloat(calculateBudgetUtilization(record.spentAmount || 0, record.budget || 0))}
            size="small"
            status={parseFloat(calculateBudgetUtilization(record.spentAmount || 0, record.budget || 0)) > 90 ? 'exception' : 'normal'}
          />
          <small style={{ color: '#888' }}>
            ${(record.spentAmount || 0).toLocaleString()} spent
          </small>
        </div>
      ),
      sorter: true,
    },
    {
      title: 'Performance',
      key: 'performance',
      render: (_, record) => (
        <div>
          <div>
            <small style={{ color: '#888' }}>Impressions:</small> {(record.impressions || 0).toLocaleString()}
          </div>
          <div>
            <small style={{ color: '#888' }}>Clicks:</small> {(record.clicks || 0).toLocaleString()} 
            <span style={{ color: '#1890ff', marginLeft: 4 }}>
              ({calculateCTR(record.clicks || 0, record.impressions || 0)}%)
            </span>
          </div>
          <div>
            <small style={{ color: '#888' }}>Conversions:</small> {record.conversions || 0}
            <span style={{ color: '#52c41a', marginLeft: 4 }}>
              ({calculateConversionRate(record.conversions || 0, record.clicks || 0)}%)
            </span>
          </div>
          <div>
            <small style={{ color: '#888' }}>Leads:</small> {record.leadsGenerated || 0}
          </div>
        </div>
      ),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => (
        <div>
          {record.startDate && (
            <div>
              <small style={{ color: '#888' }}>Start:</small> {dayjs(record.startDate).format('MM/DD/YYYY')}
            </div>
          )}
          {record.endDate && (
            <div>
              <small style={{ color: '#888' }}>End:</small> {dayjs(record.endDate).format('MM/DD/YYYY')}
            </div>
          )}
        </div>
      ),
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button 
            icon={<BarChartOutlined />} 
            onClick={() => handleAnalytics(record)}
            size="small"
            type="primary"
          >
            Analytics
          </Button>
          <Button 
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Metrics Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Campaigns"
              value={metrics.totalCampaigns}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Campaigns"
              value={metrics.activeCampaigns}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Budget"
              value={metrics.totalBudget}
              prefix={<DollarOutlined />}
              precision={0}
              formatter={(value) => `$${value?.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Spent"
              value={metrics.totalSpent}
              precision={0}
              valueStyle={{ color: '#f5222d' }}
              formatter={(value) => `$${value?.toLocaleString()}`}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Impressions"
              value={metrics.totalImpressions}
              prefix={<EyeOutlined />}
              formatter={(value) => value?.toLocaleString()}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Clicks"
              value={metrics.totalClicks}
              formatter={(value) => value?.toLocaleString()}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg CTR"
              value={metrics.avgClickThroughRate}
              precision={2}
              suffix="%"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Conversion Rate"
              value={metrics.avgConversionRate}
              precision={2}
              suffix="%"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Input
              placeholder="Search campaigns..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="DRAFT">Draft</Option>
              <Option value="ACTIVE">Active</Option>
              <Option value="PAUSED">Paused</Option>
              <Option value="COMPLETED">Completed</Option>
              <Option value="CANCELLED">Cancelled</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by Type"
              value={typeFilter}
              onChange={setTypeFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="EMAIL_CAMPAIGN">Email Campaign</Option>
              <Option value="SOCIAL_MEDIA">Social Media</Option>
              <Option value="ADVERTISING">Advertising</Option>
              <Option value="REFERRAL">Referral</Option>
              <Option value="CONTENT_MARKETING">Content Marketing</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Create Campaign
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={campaigns}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={(pagination, filters, sorter) => {
            setPagination(pagination);
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Campaign Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="Campaign Type" rules={[{ required: true }]}>
                <Select>
                  <Option value="EMAIL_CAMPAIGN">Email Campaign</Option>
                  <Option value="SOCIAL_MEDIA">Social Media</Option>
                  <Option value="ADVERTISING">Advertising</Option>
                  <Option value="REFERRAL">Referral</Option>
                  <Option value="CONTENT_MARKETING">Content Marketing</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Option value="DRAFT">Draft</Option>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="PAUSED">Paused</Option>
                  <Option value="COMPLETED">Completed</Option>
                  <Option value="CANCELLED">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="budget" label="Budget">
                <Input type="number" prefix="$" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="dateRange" label="Campaign Duration">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="targetAudience" label="Target Audience">
            <Input />
          </Form.Item>

          <Form.Item name="content" label="Campaign Content">
            <TextArea rows={4} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="imageUrl" label="Image URL">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="landingPageUrl" label="Landing Page URL">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Analytics Update Modal */}
      <Modal
        title="Update Campaign Analytics"
        open={isAnalyticsModalVisible}
        onOk={handleAnalyticsUpdate}
        onCancel={() => setIsAnalyticsModalVisible(false)}
      >
        <Form form={analyticsForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="impressions" label="Impressions" rules={[{ required: true }]}>
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="clicks" label="Clicks" rules={[{ required: true }]}>
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="conversions" label="Conversions" rules={[{ required: true }]}>
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="leadsGenerated" label="Leads Generated" rules={[{ required: true }]}>
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default MarketingManagement; 